import React from 'react';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { twJoin } from 'tailwind-merge';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import Container from '../../shared/Container';
import OptimizedImage from '../../shared/OptimizedImage';
import { MulticolumnLayoutTile } from './types/MulticolumnLayoutTile';

export default function MulticolumnLayout({ tile }: { tile: MulticolumnLayoutTile }) {
  const cardsLength = tile['content-cards'].chunks.length;
  const alignLeft = tile['align-left']?.value;
  const isImage = tile['is-image']?.value;
  const imageAspectRatio = tile['image-aspect-ratio']?.options?.[0]?.value;
  const removeTopMargin = tile['remove-top-margin']?.value;

  let aspectRatioClass = '';
  switch (imageAspectRatio) {
    case '16:9':
      aspectRatioClass = 'aspect-[16/9]';
      break;
    case '9:16':
      aspectRatioClass = 'aspect-[9/16]';
      break;
    case '1:1':
      aspectRatioClass = 'aspect-[1/1]';
      break;
    case '4:3':
      aspectRatioClass = 'aspect-[4/3]';
      break;
    case '3:4':
      aspectRatioClass = 'aspect-[3/4]';
      break;
    default:
      aspectRatioClass = 'aspect-[16/9]';
      break;
  }

  return (
    <Container className={twJoin(removeTopMargin && 'mt-0 sm:mt-0 lg:mt-0')}>
      <div
        className={twJoin(
          'mx-auto grid grid-cols-1 gap-8 sm:grid-cols-2',
          !alignLeft && 'text-center',
          cardsLength === 2 && 'lg:max-w-[60%] lg:grid-cols-2',
          cardsLength === 3 && 'lg:grid-cols-3',
          cardsLength === 4 && 'lg:grid-cols-4',
        )}
      >
        {tile['content-cards'].chunks.map((card, index: number) => {
          const image = extractComponentFromChunk(card, 'image', 'ImageContent');
          const title = extractComponentFromChunk(card, 'title', 'SingleLineContent')?.text;
          const titleColor = extractComponentFromChunk(card, 'title-color', 'SelectionContent')
            ?.options?.[0]?.value;
          const subtitle = extractComponentFromChunk(card, 'subtitle', 'SingleLineContent')?.text;
          const subtitleColor = extractComponentFromChunk(
            card,
            'subtitle-color',
            'SelectionContent',
          )?.options?.[0]?.value;
          const description = extractComponentFromChunk(card, 'description', 'RichTextContent');

          let titleColorClass = '';
          switch (titleColor) {
            case 'black':
              titleColorClass = 'text-neutral-100';
              break;
            case 'gray':
              titleColorClass = 'text-neutral-50';
              break;
            case 'yellow':
              titleColorClass = 'text-primary-100';
              break;
            default:
              titleColorClass = 'text-neutral-100';
              break;
          }

          let subtitleColorClass = '';
          switch (subtitleColor) {
            case 'black':
              subtitleColorClass = 'text-neutral-100';
              break;
            case 'gray':
              subtitleColorClass = 'text-neutral-50';
              break;
            case 'yellow':
              subtitleColorClass = 'text-primary-100';
              break;
            default:
              subtitleColorClass = 'text-neutral-100';
              break;
          }

          return (
            <div
              className={twJoin(
                cardsLength === 3 &&
                  index === cardsLength - 1 &&
                  'sm:col-span-2 sm:mx-auto sm:w-3/4 lg:col-span-1 lg:mx-0 lg:w-full',
              )}
              key={index}
            >
              {/* Big Image */}
              {isImage && (
                <OptimizedImage
                  imageComponent={image}
                  width={50}
                  height={50}
                  className={twJoin('w-full rounded-xl object-cover', aspectRatioClass)}
                  sizes="300px"
                />
              )}
              {/* Icon */}
              {!isImage && (
                <OptimizedImage
                  imageComponent={image}
                  width={50}
                  height={50}
                  className={twJoin(!alignLeft ? 'mx-auto' : '', 'mb-4')}
                  sizes="50px"
                />
              )}
              {title && (
                <h3
                  className={twJoin(
                    'mt-2 text-lg font-bold sm:text-xl md:text-2xl',
                    titleColorClass,
                  )}
                >
                  {title}
                </h3>
              )}
              {subtitle && (
                <h4 className={twJoin('mb-1 text-md', subtitleColorClass)}>{subtitle}</h4>
              )}
              <RichTextRenderer blocks={description} className="mt-1" />
            </div>
          );
        })}
      </div>
    </Container>
  );
}
